import _ from 'lodash';
import { useDebounce } from 'utils/hooks';
import { useEffect, useState } from 'react';
import { copyToClipboard } from 'utils/copyToClipboard';
import { CreativeTemplatesEntities, Views } from '../../../../../../../constants';
import { TextAsset, CarouselCreative, CarouselSlide } from '../../../../../../../types';
import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import { getErrorsText, Severity, usePushNotifications } from 'services/NotificationService';
import { useRemoveTextAssetFromArticleGroup } from 'api/kms/articleGroups/removeTextAssetFromArticleGroup.hooks';
import {
  AssetTypeEnum,
  EditTextAssetForArticleGroupMutation_editTextAssetForArticleGroup_textAsset,
  PostMaloneAssetTypeEnum,
  PostMaloneScoreSourceTypeEnum,
  useCalculatePostMaloneScoreMutation,
  useEditTextAssetForArticleGroup
} from 'api';
import { getTextAssetMaxLengthByTab } from '../../../../../../../utils/textAssetsMaxLength';

export const useTextAssetListItem = (textAsset: TextAsset) => {
  const store = useCreativeTemplatesBuilderStore();
  const notify = usePushNotifications();

  const articleGroup = store.articleGroup;
  const postMaloneProject = articleGroup?.postMaloneProject;
  const activeTabKey = store.activeTab.key;
  const isShowingPostMaloneScore =
    [CreativeTemplatesEntities.PostTexts, CreativeTemplatesEntities.Headlines].includes(activeTabKey) &&
    !!postMaloneProject;

  const handleSelect = () => {
    if (store.view === Views.ImageOrVideoCreativeTemplateEditor) {
      store.selectTextAsset(textAsset);
    }

    if (store.view === Views.CarouselCreativeTemplateEditor) {
      if (store.activeTab.key === CreativeTemplatesEntities.CTAs) {
        const updatedCarousels = _.map(store.carousels, (carousel) =>
          carousel.id === store.activeCarouselId ? { ...carousel, callToAction: textAsset } : carousel
        );
        store.setCarousels(updatedCarousels as CarouselCreative[]);
        return;
      }
      if (store.activeTab.key === CreativeTemplatesEntities.PostTexts) {
        const updatedCarousels = _.map(store.carousels, (carousel) =>
          carousel.id === store.activeCarouselId ? { ...carousel, message: textAsset } : carousel
        );
        store.setCarousels(updatedCarousels as CarouselCreative[]);
        return;
      }
      const updatedCarousels = _.map(store.carousels, mapCarousels);
      store.setCarousels(updatedCarousels as CarouselCreative[]);
    }
  };

  const mapCarousels = (carousel: CarouselCreative) => {
    const updatedSlides = _.map(carousel.carouselAssetSpecs, mapSlides);
    return { ...carousel, carouselAssetSpecs: updatedSlides };
  };

  const mapSlides = (slide: CarouselSlide) => {
    if (slide.id !== store.activeSlideId) return slide;

    const tab = activeTab();
    return { ...slide, textAssets: { ...slide.textAssets, [tab]: textAsset } };
  };

  const activeTab = () => {
    switch (store.activeTab.key) {
      case CreativeTemplatesEntities.Headlines:
        return 'headline';
      case CreativeTemplatesEntities.PostTexts:
        return 'postText';
      case CreativeTemplatesEntities.Descriptions:
        return 'description';
      default:
        return '';
    }
  };

  const textAssets = [...(store.imageOrVideoCreativeTemplateInputs[activeTabKey] as TextAsset[])];
  const isSuggestion = textAsset.postMaloneScore?.sourceType === PostMaloneScoreSourceTypeEnum.post_malone_suggestion;

  const isSelected = () => {
    switch (store.view) {
      case Views.ImageOrVideoCreativeTemplateEditor:
        return _.some(textAssets, { id: textAsset.id });
      case Views.CarouselCreativeTemplateEditor:
        const tabSelections = store.carouselSelections[activeTabKey];
        return _.some(tabSelections, { id: textAsset.id });
      default:
        return false;
    }
  };

  //
  // Delete Text Asset
  //

  const onRemoveTextAssetCompleted = () => {
    store.removeTextAsset(activeTabKey, textAsset);
  };

  const onRemoveTextAssetError = (errors: string[]) => {
    notify({
      title: 'Failed to remove TextAsset',
      message: getErrorsText(errors),
      severity: Severity.ERROR
    });
  };

  const [removeTextAssetMutation, { loading: isRemoving }] = useRemoveTextAssetFromArticleGroup({
    onCompleted: onRemoveTextAssetCompleted,
    onError: onRemoveTextAssetError
  });

  const handleRemove = () => {
    if (!articleGroup?.id) return;

    const assetType = getAssetType();
    if (!assetType) return;

    removeTextAssetMutation({
      articleGroupId: articleGroup.id.toString(),
      textAssetId: textAsset.id,
      assetType: assetType
    });
  };

  //
  // Copy to clipboard
  //

  const handleCopyToClipboard = () => {
    copyToClipboard(textAsset.text);
  };

  //
  // Editing
  //

  const [editedTextAsset, changeEditedTextAsset] = useState<TextAsset | null>(null);
  const debouncedText = useDebounce(editedTextAsset?.text, 500);

  const handleOpenEditMode = () => {
    changeEditedTextAsset({ ...textAsset });
  };

  const handleExitEditMode = () => {
    changeEditedTextAsset(null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedTextAsset) return;

    changeEditedTextAsset({ ...editedTextAsset, text: event.target.value });
  };

  //
  // Calculating Post Malone Score
  //

  const onCompletedCalculating = (engagementScore: number) => {
    if (!editedTextAsset) return;

    changeEditedTextAsset({
      ...editedTextAsset,
      postMaloneScore: {
        engagementScore: engagementScore,
        sourceType: PostMaloneScoreSourceTypeEnum.manual
      }
    });
  };

  const onErrorCalculating = (errors: string[]) => {
    notify({
      title: 'Failed to get a score',
      message: getErrorsText(errors),
      severity: Severity.ERROR
    });
  };

  const [calculateTextAssetScore, { loading: isCalculating }] = useCalculatePostMaloneScoreMutation({
    onCompleted: onCompletedCalculating,
    onError: onErrorCalculating
  });

  useEffect(() => {
    if (!postMaloneProject || !debouncedText) return;

    const assetType = getPostMaloneAssetType();
    if (!assetType) return;

    calculateTextAssetScore({
      externalProjectId: postMaloneProject.externalProjectId.toString(),
      text: debouncedText,
      assetType: assetType
    });
  }, [debouncedText]);

  //
  // Saving Edited text asset
  //

  const onEditCompleted = (_textAsset: EditTextAssetForArticleGroupMutation_editTextAssetForArticleGroup_textAsset) => {
    store.removeTextAsset(activeTabKey, textAsset);
    store.addTextAsset(activeTabKey, _textAsset);
  };

  const onEditError = (errors: string[]) => {
    notify({
      title: 'Failed to edit Text Asset',
      message: getErrorsText(errors),
      severity: Severity.ERROR
    });
  };

  const [saveEditedTextAsset, { loading: isSaving }] = useEditTextAssetForArticleGroup({
    onCompleted: onEditCompleted,
    onError: onEditError
  });

  const handleSave = () => {
    if (!articleGroup?.id || !editedTextAsset) return;

    const assetType = getAssetType();
    if (!assetType) return;

    saveEditedTextAsset({
      articleGroupId: articleGroup.id.toString(),
      postMaloneProjectId: postMaloneProject?.id?.toString(),
      assetType: assetType,
      text: editedTextAsset.text,
      textAssetId: textAsset.id,
      engagementScore: editedTextAsset?.postMaloneScore?.engagementScore
    });
  };

  //
  // Utils
  //

  const getAssetType = () => {
    switch (activeTabKey) {
      case CreativeTemplatesEntities.Headlines:
        return AssetTypeEnum.headline;
      case CreativeTemplatesEntities.PostTexts:
        return AssetTypeEnum.post_text;
      case CreativeTemplatesEntities.Descriptions:
        return AssetTypeEnum.description;
    }
  };

  const getPostMaloneAssetType = () => {
    switch (activeTabKey) {
      case CreativeTemplatesEntities.Headlines:
        return PostMaloneAssetTypeEnum.facebookHeadline;
      case CreativeTemplatesEntities.PostTexts:
        return PostMaloneAssetTypeEnum.facebookPrimaryText;
    }
  };

  const maxLengthLabelAttrs = (): Record<string, unknown> | undefined => {
    if (!textAsset) return;

    const maxTextLength = getTextAssetMaxLengthByTab(activeTabKey);

    return {
      helperText: `${textAsset.text.length}/${maxTextLength}`,
      inputProps: { maxLength: maxTextLength }
    };
  };

  return {
    postMaloneProject,
    activeTabKey,
    editedTextAsset,
    isShowingPostMaloneScore,
    isRemoving,
    isSelected: isSelected(),
    isCalculating,
    isSaving,
    isSuggestion,
    maxLengthLabelAttrs,
    handleOpenEditMode,
    handleExitEditMode,
    handleInputChange,
    handleSelect,
    handleRemove,
    handleCopyToClipboard,
    handleSave
  };
};
